import {CommonModule, DatePipe, NgClass, NgFor, NgIf, NgStyle} from "@angular/common";
import {HttpErrorResponse} from "@angular/common/http";
import {Component, HostListener, inject, Input, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import $ from 'jquery';
import {debounceTime, finalize, merge, Subject, takeUntil} from "rxjs";
import {OpportunityService} from "../../../../core/services/opportunity.service";
import {PageConfigurationService} from "../../../../core/services/page-configuration.service";
import {BookingErrorDetail, OpportunityDetails} from "../../../../core/types/booking.types";
import {PageConfiguration, PageConfigurationResponse} from "../../../../core/types/page-configuration.types";
import {BillingDetails, MerchantPlatformSettings, PaymentTokenData} from "../../../../core/types/payment.types";
import {EntityResponse} from "../../../../core/types/types";
import {CreditCardImage, getAcceptedCreditCards} from "../../../../core/utils/credit-card.utils";
import {AddNewInputFieldComponent} from "../../../add-new-input-field/add-new-input-field.component";
import {PriceAndCargoComponent} from "../../../price-and-cargo/price-and-cargo.component";
import {OrderSummaryComponent} from "../../../order-summary/order-summary.component";
import {Collapse} from 'bootstrap';
import {CustomerService} from "../../../../core/services/customer.service";
import {ModalService} from "../../../../core/services/modal.service";
import {
    PaymentCardInputWrapperComponent
} from '../../../payment/payment-card-input-wrapper/payment-card-input-wrapper.component';
import {FormValueSyncDirective} from "../../../../core/directives/form-value-sync.directive";
import {FormValueSyncRegistry} from "../../../../core/directives/form-value-sync.registry";
import {DraftGroups, FormDraftService} from "../../../../core/services/form-draft.service";
import {AuthService} from "../../../../core/auth/auth.service";
import {FormMaxLengths} from "../../../../core/validators/form-max-lengths";
import countries from "../../../../core/data/countries";
import {signatureNameValidator} from "../../../../core/validators/signature-name.validator";
import {strictEmailValidator} from "../../../../core/validators/strict-email.validator";
import {SignatureInputComponent} from "../../../shared/signature-input/signature-input.component";
import {DateInputComponent} from "../../../shared/date-input/date-input.component";
import {TermsDisplayComponent} from "../../../shared/terms-display/terms-display.component";
import {formatMDY, toDate} from "../../../../core/utils/date.utils";
import {CustomerBillingDetails} from '../../../../core/types/customer.types';

const PHONE_DISABLED_TOOLTIP = "To change the main phone number please contact your sales agent.";

interface BookingPage {
    page: number;
    form: FormGroup | null;
    stageId: string;
    title: string;
    titleMobile?: string;
}

@Component({
    selector: "app-booking",
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        NgIf,
        NgClass,
        NgFor,
        NgStyle,
        DatePipe,
        PriceAndCargoComponent,
        AddNewInputFieldComponent,
        OrderSummaryComponent,
        PaymentCardInputWrapperComponent,
        FormValueSyncDirective,
        SignatureInputComponent,
        DateInputComponent,
        TermsDisplayComponent
    ],
    templateUrl: "./booking.component.html",
    styleUrl: "./booking.component.scss",
})
export abstract class BookingComponent implements OnInit, OnDestroy {
    private _modalService = inject(ModalService);
    private _authService = inject(AuthService);
    private _formValueSync = inject(FormValueSyncRegistry);
    private _formDraft = inject(FormDraftService);

    @ViewChild(PaymentCardInputWrapperComponent) paymentCardWrapper?: PaymentCardInputWrapperComponent;

    readonly maxLengths = FormMaxLengths;
    readonly phoneDisabledTooltip = PHONE_DISABLED_TOOLTIP;
    readonly countriesList = countries;

    @Input() onAuthSuccess?: () => void;
    customerForm: FormGroup | null = null;
    originForm: FormGroup | null = null;
    destinationForm: FormGroup | null = null;
    creditCardForm: FormGroup | null = null;
    signatureForm: FormGroup | null = null;

    opportunity: OpportunityDetails | null = null;
    opportunityService!: OpportunityService;
    configurationService!: PageConfigurationService;

    private _customerService = inject(CustomerService);

    configuration: PageConfiguration | null = null;

    isPhone2Added: boolean = false;
    isOriginContactPhone2Added: boolean = false;
    isOriginContactPhone3Added: boolean = false;
    isDestinationContactPhone2Added: boolean = false;
    isDestinationContactPhone3Added: boolean = false;

    paymentStepRequired: boolean = false;
    showFormErrors: boolean = false;

    currentPage: number = 1;
    pages: BookingPage[] = [];

    acceptedCreditCards: CreditCardImage[] = [];
    merchantPlatform?: MerchantPlatformSettings;

    private _destroy$ = new Subject<void>();

    paymentMethodAddInProgress: boolean = false;
    bookingInProgress: boolean = false;

    alreadyBookedOrder: boolean = false;
    showSummary: boolean = false;

    ngOnInit(): void {
        this.fetchConfiguration();
        this.fetchOpportunity();
    }

    fetchConfiguration(): void {
        this.configurationService.pageConfiguration$
            .pipe(takeUntil(this._destroy$))
            .subscribe((response: PageConfigurationResponse | null) => {
                if (response && response.success) {
                    this.configuration = response.data!;
                    this.acceptedCreditCards = getAcceptedCreditCards(response.data!.paymentConfiguration.acceptedCreditCards);
                }
            });
    }

    fetchOpportunity(): void {
        this.opportunityService.opportunity$
            .pipe(takeUntil(this._destroy$))
            .subscribe((response: OpportunityDetails) => {
                this.opportunity = response;
                if (response.signatureDate) {
                    this.alreadyBookedOrder = true;
                    this.showSummary = true;
                    // Signed elsewhere since this draft was written; it can only
                    // mislead now.
                    this._clearDraft();
                } else {
                    // Only create forms if there's no signed document
                    this.merchantPlatform = response.merchantPlatformCredentials;

                    this.paymentStepRequired = response.isPaymentStepRequired;

                    this.createForms();
                    this.initializePages();
                    this.populateForms();
                    this._startDraftAutosave();
                }
            });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    /**
     * Scoped to the opportunity so a different booking opened in the same tab can
     * never be handed another customer's details. Null until details arrive.
     */
    private get _draftKey(): string | null {
        return this.opportunity ? `edoc-draft:booking:${this.opportunity.id}` : null;
    }

    /** Payment and signature are deliberately absent — neither is ever persisted. */
    private get _draftGroups(): DraftGroups {
        return {
            customer: this.customerForm ?? undefined,
            origin: this.originForm ?? undefined,
            destination: this.destinationForm ?? undefined
        };
    }

    /**
     * Debounced so typing on a phone doesn't write to storage on every keystroke.
     * Subscribed only after populateForms() has run, so the server's own values
     * don't immediately overwrite a draft that is about to be restored over them.
     */
    private _startDraftAutosave(): void {
        const groups = Object.values(this._draftGroups).filter((group): group is FormGroup => !!group);
        if (!groups.length) return;

        merge(...groups.map((group: FormGroup) => group.valueChanges))
            .pipe(debounceTime(400), takeUntil(this._destroy$))
            .subscribe(() => this._saveDraft());
    }

    private _saveDraft(): void {
        const key = this._draftKey;
        if (!key || this.alreadyBookedOrder || this.showSummary) return;

        this._formDraft.save(key, this._draftGroups);
    }

    private _clearDraft(): void {
        const key = this._draftKey;
        if (key) this._formDraft.clear(key);
    }

    @HostListener('document:visibilitychange')
    onVisibilityChange(): void {
        // Fires in both directions; only the trip to hidden precedes a discard.
        if (document.visibilityState === 'visible') return;

        this.flushDraft();
    }

    /**
     * Backgrounding and unload are the last dependable moments before iOS freezes
     * or discards the tab, and a debounced write may still be pending — so write
     * synchronously here rather than hoping the timer gets to run.
     *
     * syncAll() goes first on purpose: a value that was autofilled or dictated but
     * never committed lives only in the DOM, and would otherwise die with the tab
     * instead of reaching the draft.
     */
    @HostListener('window:pagehide')
    flushDraft(): void {
        this._formValueSync.syncAll();
        this._saveDraft();
    }

    goToNextStage(): void {
        if (this.currentPage >= this.pages.length) return;

        // Adopt anything sitting in the DOM but not yet in a control (autofill
        // that emitted no `input` event) so this stage is validated against
        // what the customer can actually see.
        this._formValueSync.syncAll();

        const currentStageIndex = this.currentPage - 1;
        const currentForm = this.pages[currentStageIndex]?.form;

        if (currentForm && currentForm.invalid) {
            this.showFormErrors = true;
            return;
        }

        this.showFormErrors = false;
        this.currentPage++;
    }

    goToPreviousStage(): void {
        if (this.currentPage <= 1) return;

        this.currentPage--;
    }

    createForms(): void {
        this.customerForm = new FormGroup({
            firstName: new FormControl(null, [Validators.required, Validators.maxLength(FormMaxLengths.Name)]),
            lastName: new FormControl(null, [Validators.maxLength(FormMaxLengths.Name)]),
            company: new FormControl(null, [Validators.maxLength(FormMaxLengths.Company)]),
            email: new FormControl(null, [Validators.required, strictEmailValidator(), Validators.maxLength(FormMaxLengths.Email)]),
            email2: new FormControl(null, [strictEmailValidator(), Validators.maxLength(FormMaxLengths.Email)]),
            phone: new FormControl({value: null, disabled: true}),
            phone2: new FormControl(null, [Validators.maxLength(FormMaxLengths.Phone)]),
            fax: new FormControl(null, [Validators.maxLength(FormMaxLengths.Fax)]),
            firstAvailablePickup: new FormControl(null, [Validators.required]),
            specialTerms: new FormControl({value: null, disabled: true}),
            commentFromShipper: new FormControl({value: null, disabled: true})
        });

        this.originForm = new FormGroup({
            address: new FormControl(null, [Validators.maxLength(FormMaxLengths.Address)]),
            address2: new FormControl(null, [Validators.maxLength(FormMaxLengths.Address)]),
            city: new FormControl(null, [Validators.maxLength(FormMaxLengths.City)]),
            state: new FormControl(null, [Validators.maxLength(FormMaxLengths.State)]),
            postalCode: new FormControl(null, [Validators.maxLength(FormMaxLengths.PostalCode)]),
            country: new FormControl(null, [Validators.maxLength(FormMaxLengths.Country)]),
            userFriendlyAddress: new FormControl({value: null, disabled: true}),
            iAmPickupContact: new FormControl(false),
            contactName: new FormControl(null, [Validators.maxLength(FormMaxLengths.ContactName)]),
            contactEmail: new FormControl(null, [strictEmailValidator(), Validators.maxLength(FormMaxLengths.Email)]),
            contactPhone: new FormControl(null, [Validators.maxLength(FormMaxLengths.ContactPhone)]),
            contactPhone2: new FormControl(null, [Validators.maxLength(FormMaxLengths.Phone)]),
            contactPhone3: new FormControl(null, [Validators.maxLength(FormMaxLengths.Phone)]),
            contactPhoneCell: new FormControl(null, [Validators.maxLength(FormMaxLengths.Phone)])
        });

        this.originControls.iAmPickupContact.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe((value: boolean) => {
                if (value) {
                    const firstName = this.customerControls.firstName.value?.trim() || '';
                    const lastName = this.customerControls.lastName.value?.trim() || '';
                    this.originControls.contactName.setValue(`${firstName} ${lastName}`.trim());
                    this.originControls.contactName.disable();
                    this.originControls.contactEmail.setValue(this.customerControls.email.value);
                    this.originControls.contactEmail.disable();
                    this.originControls.contactPhone.setValue(this.customerControls.phone.value);
                    this.originControls.contactPhone.disable();
                }
                else {
                    this.originControls.contactName.setValue(null);
                    this.originControls.contactName.enable();
                    this.originControls.contactEmail.setValue(null);
                    this.originControls.contactEmail.enable();
                    this.originControls.contactPhone.setValue(null);
                    this.originControls.contactPhone.enable();
                }
        });

        this.destinationForm = new FormGroup({
            address: new FormControl(null, [Validators.maxLength(FormMaxLengths.Address)]),
            address2: new FormControl(null, [Validators.maxLength(FormMaxLengths.Address)]),
            city: new FormControl(null, [Validators.maxLength(FormMaxLengths.City)]),
            state: new FormControl(null, [Validators.maxLength(FormMaxLengths.State)]),
            postalCode: new FormControl(null, [Validators.maxLength(FormMaxLengths.PostalCode)]),
            country: new FormControl(null, [Validators.maxLength(FormMaxLengths.Country)]),
            userFriendlyAddress: new FormControl({value: null, disabled: true}),
            iAmPickupContact: new FormControl(false),
            contactName: new FormControl(null, [Validators.maxLength(FormMaxLengths.ContactName)]),
            contactEmail: new FormControl(null, [strictEmailValidator(), Validators.maxLength(FormMaxLengths.Email)]),
            contactPhone: new FormControl(null, [Validators.maxLength(FormMaxLengths.ContactPhone)]),
            contactPhone2: new FormControl(null, [Validators.maxLength(FormMaxLengths.Phone)]),
            contactPhone3: new FormControl(null, [Validators.maxLength(FormMaxLengths.Phone)]),
            contactPhoneCell: new FormControl(null, [Validators.maxLength(FormMaxLengths.Phone)])
        });

        this.destinationControls.iAmPickupContact.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe((value: boolean) => {
                if (value) {
                    const firstName = this.customerControls.firstName.value?.trim() || '';
                    const lastName = this.customerControls.lastName.value?.trim() || '';
                    this.destinationControls.contactName.setValue(`${firstName} ${lastName}`.trim());
                    this.destinationControls.contactName.disable();
                    this.destinationControls.contactEmail.setValue(this.customerControls.email.value);
                    this.destinationControls.contactEmail.disable();
                    this.destinationControls.contactPhone.setValue(this.customerControls.phone.value);
                    this.destinationControls.contactPhone.disable();
                }
                else {
                    this.destinationControls.contactName.setValue(null);
                    this.destinationControls.contactName.enable();
                    this.destinationControls.contactEmail.setValue(null);
                    this.destinationControls.contactEmail.enable();
                    this.destinationControls.contactPhone.setValue(null);
                    this.destinationControls.contactPhone.enable();
                }
        });

        this.creditCardForm = new FormGroup({
            address: new FormControl(null, [Validators.required, Validators.maxLength(FormMaxLengths.Address)]),
            city: new FormControl(null, [Validators.required, Validators.maxLength(FormMaxLengths.City)]),
            state: new FormControl(null, [Validators.required, Validators.maxLength(2), Validators.minLength(2)]),
            postalCode: new FormControl(null, [Validators.required, Validators.maxLength(FormMaxLengths.PostalCode)]),
            country: new FormControl('US', [Validators.required, Validators.maxLength(2)])
        });

        this.signatureForm = new FormGroup({
            signedBy: new FormControl(null, [Validators.required, signatureNameValidator()]),
            ip: new FormControl({value: null, disabled: true}),
            signedOn: new FormControl({value: null, disabled: true}),
            agree: new FormControl(false, [Validators.requiredTrue])
        });
    }

    populateForms(): void {
        if (!this.opportunity) return;

        this.customerForm?.patchValue({
            ...this.opportunity.customer,
            firstName: this.opportunity.customer.firstName?.trim(),
            lastName: this.opportunity.customer.lastName?.trim(),
            firstAvailablePickup: this.opportunity.firstAvailablePickupDate,
            specialTerms: this.opportunity.specialTerms,
            commentFromShipper: this.opportunity.commentFromShipper
        });
        this.originForm?.patchValue(this.opportunity.origin);
        this.destinationForm?.patchValue(this.opportunity.destination);

        if (this.customerHasCreditCard) {
            this.creditCardForm?.disable();
        } else {
            this.creditCardForm?.patchValue({
                address: this.opportunity.customer.address,
                city: this.opportunity.customer.city,
                state: this.opportunity.customer.state,
                postalCode: this.opportunity.customer.postalCode,
                country: this.opportunity.customer.country?.slice(0, 2) || 'US'
            });
        }

        this.signatureForm?.patchValue({
            ip: this.opportunity.clientIp,
            signedOn: new Date()
        });

        // Anything the customer had already entered before the tab was reloaded wins
        // over the server's copy. This sits above the reveal flags below so restored
        // values light up their own "Add Another ..." slots through the same code.
        const draftKey = this._draftKey;
        if (draftKey) this._formDraft.restore(draftKey, this._draftGroups);

        // Cascading "Add Another Phone" slots are gated by these flags, which otherwise
        // only flip on the user's button click. Without this, backend-loaded values for
        // fax / contactPhone3 / contactPhoneCell sit on the form but their inputs are
        // never rendered. OR-chain so a later value still reveals the slot above it.
        this.isPhone2Added = !!this.customerControls.fax.value;
        this.isOriginContactPhone2Added = !!this.originControls.contactPhone3.value
            || !!this.originControls.contactPhoneCell.value;
        this.isOriginContactPhone3Added = !!this.originControls.contactPhoneCell.value;
        this.isDestinationContactPhone2Added = !!this.destinationControls.contactPhone3.value
            || !!this.destinationControls.contactPhoneCell.value;
        this.isDestinationContactPhone3Added = !!this.destinationControls.contactPhoneCell.value;
    }

    initializePages(): void {
        if (this.paymentStepRequired) {
            this.pages = [
                {
                    page: 1,
                    form: this.customerForm,
                    stageId: 'order-info',
                    title: 'Order Info'
                },
                {
                    page: 2,
                    form: this.originForm,
                    stageId: 'origin',
                    title: 'Origin'
                },
                {
                    page: 3,
                    form: this.destinationForm,
                    stageId: 'destination',
                    title: 'Destination'
                },
                {
                    page: 4,
                    form: this.creditCardForm,
                    stageId: 'payment',
                    title: 'Payment'
                },
                {
                    page: 5,
                    form: this.signatureForm,
                    stageId: 'terms-condition',
                    title: 'Terms & Condition',
                    titleMobile: 'T&C'
                }
            ];
        } else {
            this.pages = [
                {
                    page: 1,
                    form: this.customerForm,
                    stageId: 'order-info',
                    title: 'Order Info'
                },
                {
                    page: 2,
                    form: this.originForm,
                    stageId: 'origin',
                    title: 'Origin'
                },
                {
                    page: 3,
                    form: this.destinationForm,
                    stageId: 'destination',
                    title: 'Destination'
                },
                {
                    page: 4,
                    form: this.signatureForm,
                    stageId: 'terms-condition',
                    title: 'Terms & Condition',
                    titleMobile: 'T&C'
                }
            ];
        }
    }

    async submitPaymentMethod() {
        this._formValueSync.syncAll();

        if (this.creditCardForm?.invalid) {
            this.showFormErrors = true;
            return;
        }

        // Validate payment card wrapper
        if (!this.paymentCardWrapper || !this.paymentCardWrapper.validate()) {
            this.showFormErrors = true;
            this._modalService.showError('Invalid Card', 'Please complete all card information');
            return;
        }

        this.paymentMethodAddInProgress = true;

        try {
            const formValue = this.creditCardForm?.value;

            const cardHolderName = this.paymentCardWrapper.getCardHolderName();

            if (!cardHolderName) {
                this.showFormErrors = true;
                this._modalService.showError('Invalid Card', 'Cardholder name is required');
                this.paymentMethodAddInProgress = false;
                return;
            }

            // Validate cardholder name format (must have at least first and last name)
            const nameParts = cardHolderName.trim().split(/\s+/);
            if (nameParts.length < 2) {
                this.showFormErrors = true;
                this._modalService.showError('Invalid Card', 'Please enter full name (e.g., John Doe)');
                this.paymentMethodAddInProgress = false;
                return;
            }

            const billingDetails: BillingDetails = {
                customerId: this.opportunity!.customer.id,
                fullName: cardHolderName,
                email: this.opportunity?.customer.email,
                phone: this.opportunity?.customer.phone,
                address: formValue.address,
                city: formValue.city,
                state: formValue.state,
                postalCode: formValue.postalCode,
                country: formValue.country || 'US'
            };

            const paymentToken: PaymentTokenData = await this.paymentCardWrapper.createPaymentMethod(billingDetails);

            paymentToken.firstName = nameParts[0];
            paymentToken.lastName = nameParts.slice(1).join(' ');
            paymentToken.address = formValue.address;
            paymentToken.city = formValue.city;
            paymentToken.state = formValue.state;
            paymentToken.postalCode = formValue.postalCode;
            paymentToken.country = formValue.country || 'US';

            this._customerService.addNewCreditCard(
                this.opportunity!.customer.id,
                paymentToken,
                false
            ).pipe(
                finalize(() => this.paymentMethodAddInProgress = false)
            )
            .subscribe({
                next: (response: EntityResponse<CustomerBillingDetails>) => {
                    this.opportunity!.customer.creditCardNumber = response.data.lastFourDigits;
                    this.goToNextStage()
                },
                error: (error: HttpErrorResponse) => {
                    this.onCcError(error);
                }
            });

        } catch (error: any) {
            this.paymentMethodAddInProgress = false;
            this._modalService.showError('Payment Error', error.message || 'Failed to process payment');
        }
    }

    submit(): void {
        // Last line of defence before the payload is built: a field filled
        // programmatically and never focused reaches this point with the value
        // in the DOM only, and would otherwise be sent as null.
        this._formValueSync.syncAll();

        if (this.signatureForm?.invalid) {
            this.showFormErrors = true;
            return;
        }

        this.bookingInProgress = true;

        const customerFormValue = this.customerForm?.value;
        // Top-level customer phone is intentionally not sent — backend now preserves
        // customer.Phone on the server side. See OrderBookingRequestHandler.
        const { phone: _omitPhone, ...customerPayload } = customerFormValue ?? {};

        const firstAvailablePickup = toDate(customerPayload.firstAvailablePickup);

        const payload = {
            ...customerPayload,
            firstName: customerPayload.firstName?.trim(),
            lastName: customerPayload.lastName?.trim() || null,
            firstAvailablePickup: firstAvailablePickup ? formatMDY(firstAvailablePickup) : null,
            origin: {
                ...this.originForm?.value,
                contactName : this.originControls.contactName.value,
                contactEmail: this.originControls.contactEmail.value,
                contactPhone: this.originControls.contactPhone.value,
                contactPhone2: this.originControls.contactPhone2.value,
                contactPhone3: this.originControls.contactPhone3.value,
                contactPhoneCell: this.originControls.contactPhoneCell.value
            },
            destination: {
                ...this.destinationForm?.value,
                contactName : this.destinationControls.contactName.value,
                contactEmail: this.destinationControls.contactEmail.value,
                contactPhone: this.destinationControls.contactPhone.value,
                contactPhone2: this.destinationControls.contactPhone2.value,
                contactPhone3: this.destinationControls.contactPhone3.value,
            },
            signature: {
                ip: this.signatureControls.ip.value,
                signedBy: this.signatureControls.signedBy.value,
                signedOnTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
            }
        };

        this.opportunityService.bookOrder(payload)
            .pipe(
                finalize(() => this.bookingInProgress = false)
            )
            .subscribe({
                next: () => {
                    this.alreadyBookedOrder = false;
                    this.showSummary = true;
                    // Booked — the draft has served its purpose and must not be
                    // offered back if this tab is reloaded.
                    this._clearDraft();
                },
                error: (error: HttpErrorResponse) => {
                    this.onError(error);
                }
        });
    }

    addressDropdownOpened: boolean = false;
    selectedAddress: string = 'My Address';

    toggleAddressDropdown(): void {
        this.addressDropdownOpened = !this.addressDropdownOpened;
    }

    setMyAddressAsBillingAddress(): void {
        if (!this.opportunity || !this.creditCardForm) return;

        this.creditCardForm.patchValue({
            address: this.opportunity.customer.address,
            city: this.opportunity.customer.city,
            state: this.opportunity.customer.state,
            postalCode: this.opportunity.customer.postalCode,
            country: this.opportunity.customer.country?.slice(0, 2) || 'US'
        });

        this.selectedAddress = 'My Address';
        this.toggleAddressDropdown();
    }

    setOriginAsBillingAddress(): void {
        if (!this.originForm || !this.creditCardForm) return;

        this.creditCardForm.patchValue({
            address: this.originControls.address.value,
            city: this.originControls.city.value,
            state: this.originControls.state.value,
            postalCode: this.originControls.postalCode.value,
            country: this.originControls.country.value?.slice(0, 2) || 'US'
        });

        this.selectedAddress = 'Origin Address';
        this.toggleAddressDropdown();
    }

    setDestinationAsBillingAddress(): void {
        if (!this.destinationForm || !this.creditCardForm) return;

        this.creditCardForm.patchValue({
            address: this.destinationControls.address.value,
            city: this.destinationControls.city.value,
            state: this.destinationControls.state.value,
            postalCode: this.destinationControls.postalCode.value,
            country: this.destinationControls.country.value?.slice(0, 2) || 'US'
        });

        this.selectedAddress = 'Destination Address';
        this.toggleAddressDropdown();
    }

    get customerControls() {
        return {
            firstName: this.customerForm?.get("firstName") as FormControl,
            lastName: this.customerForm?.get("lastName") as FormControl,
            company: this.customerForm?.get("company") as FormControl,
            email: this.customerForm?.get("email") as FormControl,
            email2: this.customerForm?.get("email2") as FormControl,
            phone: this.customerForm?.get("phone") as FormControl,
            phone2: this.customerForm?.get("phone2") as FormControl,
            fax: this.customerForm?.get("fax") as FormControl,
            firstAvailablePickup: this.customerForm?.get("firstAvailablePickup") as FormControl,
            specialTerms: this.customerForm?.get("specialTerms") as FormControl,
            commentFromShipper: this.customerForm?.get("commentFromShipper") as FormControl
        }
    }

    get originControls() {
        return {
            address: this.originForm?.get("address") as FormControl,
            address2: this.originForm?.get("address2") as FormControl,
            city: this.originForm?.get("city") as FormControl,
            state: this.originForm?.get("state") as FormControl,
            postalCode: this.originForm?.get("postalCode") as FormControl,
            country: this.originForm?.get("country") as FormControl,
            userFriendlyAddress: this.originForm?.get("userFriendlyAddress") as FormControl,
            iAmPickupContact: this.originForm?.get("iAmPickupContact") as FormControl,
            contactName: this.originForm?.get("contactName") as FormControl,
            contactEmail: this.originForm?.get("contactEmail") as FormControl,
            contactPhone: this.originForm?.get("contactPhone") as FormControl,
            contactPhone2: this.originForm?.get("contactPhone2") as FormControl,
            contactPhone3: this.originForm?.get("contactPhone3") as FormControl,
            contactPhoneCell: this.originForm?.get("contactPhoneCell") as FormControl
        }
    }

    get destinationControls() {
        return {
            address: this.destinationForm?.get("address") as FormControl,
            address2: this.destinationForm?.get("address2") as FormControl,
            city: this.destinationForm?.get("city") as FormControl,
            state: this.destinationForm?.get("state") as FormControl,
            postalCode: this.destinationForm?.get("postalCode") as FormControl,
            country: this.destinationForm?.get("country") as FormControl,
            userFriendlyAddress: this.destinationForm?.get("userFriendlyAddress") as FormControl,
            iAmPickupContact: this.destinationForm?.get("iAmPickupContact") as FormControl,
            contactName: this.destinationForm?.get("contactName") as FormControl,
            contactEmail: this.destinationForm?.get("contactEmail") as FormControl,
            contactPhone: this.destinationForm?.get("contactPhone") as FormControl,
            contactPhone2: this.destinationForm?.get("contactPhone2") as FormControl,
            contactPhone3: this.destinationForm?.get("contactPhone3") as FormControl,
            contactPhoneCell: this.destinationForm?.get("contactPhoneCell") as FormControl
        }
    }

    get creditCardControls() {
        return {
            address: this.creditCardForm?.get("address") as FormControl,
            city: this.creditCardForm?.get("city") as FormControl,
            state: this.creditCardForm?.get("state") as FormControl,
            postalCode: this.creditCardForm?.get("postalCode") as FormControl,
            country: this.creditCardForm?.get("country") as FormControl
        }
    }

    get signatureControls() {
        return {
            signedBy: this.signatureForm?.get("signedBy") as FormControl,
            ip: this.signatureForm?.get("ip") as FormControl,
            signedOn: this.signatureForm?.get("signedOn") as FormControl,
            agree: this.signatureForm?.get("agree") as FormControl
        }
    }

    get terms(): string | undefined {
        return this.configuration?.paymentConfiguration.terms?.replace(/\n/g, '<br>');
    }

    get customerHasCreditCard(): boolean {
        return this.opportunity?.customer.creditCardNumber != null;
    }

    get displayShipperNote(): boolean {
        return !!this.customerControls.commentFromShipper.value
            && this.configuration?.tenantId !== 409
            && !(this.customerControls.commentFromShipper.value as string)?.includes('utm_source');
    }

    onError(error: HttpErrorResponse): void {
        const signOut = () => this._authService.signOut();
        const detail = error.error?.detail;

        if (detail === BookingErrorDetail.UniqueConstraintViolation || error.status === 412) {
            this._modalService.showError('Booking failed', `${error.error?.title} Please refresh the page and try again`, signOut);
            return;
        }

        this._modalService.showError("Booking failed", error.error?.title, () => signOut);
    }

    onCcError(error: HttpErrorResponse): void {
        this._modalService.showError("Unable to add card", `${error.error?.title} Please check your card details and try again.`);
    }

    onCreditCardStateInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const upper = input.value.toUpperCase();
        if (upper !== input.value) {
            input.value = upper;
            this.creditCardControls.state.setValue(upper);
        }
    }

    @HostListener('window:resize')
    onResize() {
        this.updateCollapseShow();
    }

    private updateCollapseShow() {
        let colapsePriseElem = document.getElementById("collapsePrice");
        let bsCollapsePrice = null;
        if (colapsePriseElem) {
            bsCollapsePrice = new Collapse(colapsePriseElem, { toggle: false });
        }

        let collapseCargoElem = document.getElementById("collapseCargo");
        let bsCollapseCargo = null;
        if (collapseCargoElem) {
            bsCollapseCargo = new Collapse(collapseCargoElem, { toggle: false });
        }

        if (!$('.transaction-stage[stage-id="order-info"]')?.hasClass('selected-stage') && ($(window).width() || 0) <= 992) {
            bsCollapsePrice && bsCollapsePrice.hide();
            bsCollapseCargo && bsCollapseCargo.hide();
        } else {
            bsCollapsePrice && bsCollapsePrice.show();
            bsCollapseCargo && bsCollapseCargo.show();
        }
    }

}

